import { Checkbox as UiCheckbox } from "@rumbledirect/ui";
import { QuestionCheckboxModel, ItemValue } from "survey-core";
import { useMemo } from "react";
import { createStyles } from "antd-style";
import { choiceIconRegistry } from "../registrations/choiceIconRegistry";

export class CheckboxGroupLargeQuestionModel extends QuestionCheckboxModel {
  getType() {
    return "checkboxGroupLarge";
  }
}

const useStyles = createStyles(({ css, token }) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginTop: 15,
  },
  item: {
    display: "flex",
    alignItems: "center",
    border: `1px solid ${token.colorBorder}`,
    borderRadius: `${token.borderRadius}px`,
    padding: "16px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: token.colorPrimary,
      boxShadow: `0 0 0 1px ${token.colorPrimary}`,
    },
  },
  itemSelected: {
    borderColor: token.colorPrimary,
    backgroundColor: token.colorPrimaryBg,
    boxShadow: `0 0 0 1px ${token.colorPrimary}`,
  },
  itemDisabled: {
    cursor: "not-allowed",
    opacity: 0.6,
  },
  checkbox: css`
    margin-right: 20px;
    pointer-events: none;

    & .ant-checkbox-inner {
      width: 32px;
      height: 32px;
    }

    & .ant-checkbox-inner::after {
      width: 12px;
      height: 20px;
    }

    input[type="checkbox"] {
      position: absolute !important;
      height: 1px !important;
      opacity: 0 !important;
    }
  `,
  content: {
    flex: 1,
    fontSize: "14px",
    lineHeight: "1.5",
  },
  icon: {
    marginLeft: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

export const CheckboxGroupLargeQuestion = (props: {
  question: CheckboxGroupLargeQuestionModel;
}) => {
  const { styles, cx } = useStyles();

  const selectedValues = useMemo(() => {
    const val = props.question.value;
    if (!val) return [];
    return Array.isArray(val) ? val : [val];
  }, [props.question.value]);

  const handleItemClick = (item: ItemValue) => {
    if (props.question.isDisabledAttr) return;

    const currentValues = [...selectedValues];
    const itemIndex = currentValues.indexOf(item.value);

    if (itemIndex === -1) {
      currentValues.push(item.value);
    } else {
      currentValues.splice(itemIndex, 1);
    }

    props.question.value = currentValues;
  };

  return (
    <div className={styles.container}>
      {props.question.visibleChoices.map((choice: ItemValue) => {
        const isSelected = selectedValues.includes(choice.value);
        const iconKey = choice.getPropertyValue?.("icon");
        const icon = iconKey ? choiceIconRegistry.get(iconKey) : undefined;

        return (
          <div
            key={choice.value}
            className={cx(
              styles.item,
              isSelected && styles.itemSelected,
              props.question.isDisabledAttr && styles.itemDisabled,
            )}
            onClick={() => handleItemClick(choice)}
          >
            <UiCheckbox
              checked={isSelected}
              disabled={props.question.isDisabledAttr}
              className={styles.checkbox}
              onChange={() => handleItemClick(choice)}
            />
            <div className={styles.content}>
              <strong>{choice.text}</strong>
              {choice.getPropertyValue?.("textExtra")}
            </div>
            {icon && <div className={styles.icon}>{icon}</div>}
          </div>
        );
      })}
    </div>
  );
};
